import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Calendar, ArrowRight, Users, Clock, Globe, Info } from "lucide-react";
import { initials } from "@/components/site/data";
import { RegisterDialog } from "@/components/site/RegisterDialog";
import { EventHeroSlider } from "@/components/site/EventHeroSlider";
import { EventsShowcase } from "@/components/site/EventsShowcase";
import { api, fetchEventCollection } from "@/lib/api";
import { useSelectedEvent } from "@/lib/event-context";
import {
  extractUniqueValues,
  getCurrentEvent,
  getUpcomingEvents,
  resolveSpeakerImageUrl,
  resolveEventImageUrl,
  formatEventDateRange,
} from "@/lib/events";
import { useState, useEffect, useMemo } from "react";

// ── Types ────────────────────────────────────────────────────────────────────

interface Speaker {
  id: number;
  name: string;
  role: string;
  image_url: string | null;
}

interface AgendaItem {
  id: number;
  time_range: string;
  title: string;
  description: string;
}

interface Sponsor {
  id: number;
  name: string;
  image_url: string | null;
}

interface AboutContent {
  id: number;
  content_type: "paragraph" | "stat";
  heading: string | null;
  subtitle: string | null;
  badge_text: string | null;
  section_heading: string | null;
  content: string | null;
  stat_value: string | null;
  stat_label: string | null;
  sort_order: number;
  image_url: string | null;
}

interface HomepageSection {
  id: number;
  section_key: string;
  eyebrow: string | null;
  title: string | null;
  subtitle: string | null;
  badge_text: string | null;
  heading: string | null;
  body_text: string | null;
  cta_primary_label: string | null;
  cta_primary_href: string | null;
  cta_secondary_label: string | null;
  cta_secondary_href: string | null;
  is_visible: boolean;
  sort_order: number;
}

type SectionMap = Record<string, HomepageSection>;

// ── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/")({
  component: Home,
  head: () => ({
    meta: [
      { title: "UASA Events — Arab Capital Markets Conferences" },
      {
        name: "description",
        content:
          "Explore conferences and events from the Union of Arab Securities Authorities across the region.",
      },
      { property: "og:title", content: "UASA Events Program" },
      {
        property: "og:description",
        content:
          "Multi-event program for Arab capital markets leaders, regulators, and innovators.",
      },
    ],
  }),
});

// ── Component ────────────────────────────────────────────────────────────────

function Home() {
  const { events, selectedEventId, selectEvent, loading: eventsLoading } = useSelectedEvent();
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [agenda, setAgenda] = useState<AgendaItem[]>([]);
  const [aboutContent, setAboutContent] = useState<AboutContent[]>([]);
  const [sections, setSections] = useState<SectionMap>({});
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  const currentEvent = useMemo(
    () => getCurrentEvent(events, selectedEventId),
    [events, selectedEventId],
  );
  const heroEvents = useMemo(() => (currentEvent ? [currentEvent] : []), [currentEvent]);
  const upcomingEvents = useMemo(
    () => getUpcomingEvents(events, currentEvent?.id),
    [events, currentEvent?.id],
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  // Fetch about content + homepage section copy in parallel
  useEffect(() => {
    const loadShared = async () => {
      try {
        const [aboutResponse, sectionsResponse] = await Promise.all([
          api.get<AboutContent[]>("/api/about"),
          api.get<HomepageSection[]>("/api/homepage-sections"),
        ]);

        if (aboutResponse.success && aboutResponse.data) {
          setAboutContent(aboutResponse.data);
        }

        if (sectionsResponse.success && sectionsResponse.data) {
          const map: SectionMap = {};
          for (const s of sectionsResponse.data) {
            map[s.section_key] = { ...s, is_visible: Boolean(s.is_visible) };
          }
          setSections(map);
        }
      } catch (error) {
        console.error("Failed to load shared content:", error);
      } finally {
        setLoading(false);
      }
    };
    loadShared();
  }, []);

  // Fetch event-specific speakers, sponsors, and agenda
  useEffect(() => {
    if (!selectedEventId) {
      setSpeakers([]);
      setSponsors([]);
      setAgenda([]);
      return;
    }

    const loadEventContent = async () => {
      try {
        const [speakerItems, agendaItems, sponsorItems] = await Promise.all([
          fetchEventCollection<Speaker>(
            `/api/events/${selectedEventId}/speakers`,
            "/api/speakers",
          ),
          fetchEventCollection<AgendaItem>(
            `/api/events/${selectedEventId}/agenda`,
            "/api/agenda",
          ),
          fetchEventCollection<Sponsor>(
            `/api/events/${selectedEventId}/sponsors`,
            "/api/sponsors",
          ),
        ]);
        setSpeakers(speakerItems);
        setAgenda(agendaItems);
        setSponsors(sponsorItems);
      } catch (error) {
        console.error("Failed to load event content:", error);
        setSpeakers([]);
        setSponsors([]);
        setAgenda([]);
      }
    };

    loadEventContent();
  }, [selectedEventId]);

  // ── Derived values ───────────────────────────────────────────────────────

  const locationCount = extractUniqueValues(events, "location").length;

  const firstParagraph = aboutContent
    .filter((item) => item.content_type === "paragraph")
    .sort((a, b) => a.sort_order - b.sort_order)[0];
  const aboutBadgeText = firstParagraph?.badge_text || "About UASA Events";
  const aboutSectionHeading =
    firstParagraph?.section_heading || "Advancing Arab Capital Markets Together";
  const aboutImageUrl = firstParagraph?.image_url
    ? firstParagraph.image_url.startsWith("http")
      ? firstParagraph.image_url
      : `${import.meta.env.VITE_API_URL || "http://localhost:3001"}${firstParagraph.image_url}`
    : null;

  // Section copy — fall back to original hardcoded strings if DB row is missing
  const sec = (key: string): HomepageSection | null => sections[key] ?? null;
  const upcomingSec  = sec("upcoming_events");
  const pastSec      = sec("past_events");
  const speakersSec  = sec("speakers_section");
  const ctaSec       = sec("cta_section");

  // ── Loading gate ─────────────────────────────────────────────────────────

  if (loading || eventsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading...
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── 1. Hero ─────────────────────────────────────────────────────── */}
      <EventHeroSlider
        events={heroEvents}
        mounted={mounted}
        onSelectEvent={(event) => selectEvent(event.id)}
      />

      {/* ── 2. About the Event ──────────────────────────────────────────── */}
      {currentEvent ? (
        <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-secondary/20 via-background to-accent/10 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

          <div className="max-w-7xl mx-auto relative">
            {/* Section header */}
            <div
              className={`text-center mb-12 sm:mb-16 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
              style={{ animationDelay: "0.1s" }}
            >
              {/* <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-xs uppercase tracking-[0.3em] mb-4">
                <Info className="w-3.5 h-3.5" />
                About the Event
              </div> */}
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary mb-4 leading-tight">
                {currentEvent.title}
              </h2>
              {currentEvent.subtitle && (
                <p className="text-muted-foreground text-base sm:text-lg italic max-w-2xl mx-auto">
                  &ldquo;{currentEvent.subtitle}&rdquo;
                </p>
              )}
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full mt-4" />
            </div>

            {/* Two-column: text + image */}
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left — description card */}
              <div
                className={`order-2 lg:order-1 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
                style={{ animationDelay: "0.2s" }}
              >
                <div className="relative">
                  <div className="absolute -inset-3 bg-gradient-to-r from-primary/20 to-accent/20 rounded-2xl blur-xl" />
                  <Card className="relative bg-card/80 backdrop-blur-sm border border-border/50 p-8 sm:p-10 lg:p-12 shadow-2xl">
                    <div className="text-muted-foreground leading-relaxed text-base sm:text-lg">
                      <p>
                        {(() => {
                          const raw = currentEvent.description
                            ? currentEvent.description.length > 400
                              ? currentEvent.description.substring(0, 400) + "…"
                              : currentEvent.description
                            : firstParagraph?.content ||
                              "The Union of Arab Securities Authorities (UASA) hosts a multi-event program bringing together officials, policymakers, and leaders from Arab and international financial institutions.";
                          // Strip HTML tags for the plain-text preview
                          return raw.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
                        })()}
                      </p>
                    </div>

                    <div className="mt-8 pt-8 border-t border-border/50 flex flex-wrap gap-4">
                      <Button
                        asChild
                        size="lg"
                        className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground font-semibold shadow-lg shadow-primary/25"
                      >
                        <Link
                          to="/events/$eventId"
                          params={{ eventId: String(currentEvent.id) }}
                          onClick={() => selectEvent(currentEvent.id)}
                        >
                          View Full Details <ArrowRight className="w-4 h-4 ml-2" />
                        </Link>
                      </Button>
                      <RegisterDialog
                        triggerLabel="Register Now"
                        triggerSize="lg"
                        triggerVariant="outline"
                        triggerClassName="border-2 border-primary/30 text-primary hover:bg-primary/10 hover:border-primary/50 transition-all"
                        eventId={currentEvent.id}
                        eventTitle={currentEvent.title}
                      />
                    </div>
                  </Card>
                </div>
              </div>

              {/* Right — event image */}
              <div
                className={`order-1 lg:order-2 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
                style={{ animationDelay: "0.3s" }}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video sm:aspect-[4/3]">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent z-10" />
                  <img
                    src={resolveEventImageUrl(currentEvent.image_url)}
                    alt={currentEvent.title}
                    className="w-full h-full object-cover"
                  />
                  {/* Metadata pill overlay */}
                  {(currentEvent.location || currentEvent.event_date) && (
                    <div className="absolute bottom-4 left-4 right-4 z-20 flex flex-wrap gap-2">
                      {currentEvent.location && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent inline-block" />
                          {currentEvent.location}
                        </span>
                      )}
                      {(currentEvent.event_date_from || currentEvent.event_date) && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent inline-block" />
                          {formatEventDateRange(currentEvent)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        /* Fallback generic about — shown when no event is selected */
        <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-secondary/20 via-background to-accent/10 relative overflow-hidden">
          <div className="max-w-7xl mx-auto relative">
            <div
              className={`text-center mb-12 sm:mb-16 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
            >
              <div className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-xs uppercase tracking-[0.3em] mb-4">
                {aboutBadgeText}
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary mb-4 leading-tight">
                {aboutSectionHeading}
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full" />
            </div>

            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div
                className={`order-2 lg:order-1 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
              >
                <div className="relative">
                  <div className="absolute -inset-3 bg-gradient-to-r from-primary/20 to-accent/20 rounded-2xl blur-xl" />
                  <Card className="relative bg-card/80 backdrop-blur-sm border border-border/50 p-8 sm:p-10 lg:p-12 shadow-2xl">
                    <div className="text-muted-foreground leading-relaxed text-base sm:text-lg">
                      <p>
                        {(() => {
                          const p = aboutContent
                            .filter((i) => i.content_type === "paragraph" && i.content)
                            .sort((a, b) => a.sort_order - b.sort_order)[0];
                          const raw =
                            p?.content ||
                            "The Union of Arab Securities Authorities (UASA) hosts a multi-event program bringing together officials, policymakers, and leaders from Arab and international financial institutions.";
                          // Strip HTML tags for the plain-text preview
                          const plain = raw.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
                          return plain.length > 300 ? plain.substring(0, 300) + "…" : plain;
                        })()}
                      </p>
                    </div>
                    <div className="mt-8 pt-8 border-t border-border/50 flex flex-wrap gap-4">
                      <Button
                        asChild
                        size="lg"
                        className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground font-semibold shadow-lg shadow-primary/25"
                      >
                        <Link to="/about">
                          Discover More <ArrowRight className="w-4 h-4 ml-2" />
                        </Link>
                      </Button>
                      <Button
                        asChild
                        size="lg"
                        variant="outline"
                        className="border-2 border-primary/30 text-primary hover:bg-primary/10 hover:border-primary/50 transition-all"
                      >
                        <Link to="/speakers">View Speakers</Link>
                      </Button>
                    </div>
                  </Card>
                </div>
              </div>

              <div
                className={`order-1 lg:order-2 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
              >
                {aboutImageUrl ? (
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    <img
                      src={aboutImageUrl}
                      alt="About UASA Events"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                ) : (
                  <div className="relative rounded-2xl bg-gradient-to-br from-primary via-primary/80 to-accent/60 p-1 shadow-2xl overflow-hidden">
                    <div className="w-full rounded-xl bg-card flex items-center justify-center p-12 sm:p-16 text-center">
                      <div>
                        <div className="font-serif text-5xl sm:text-6xl text-primary mb-4">
                          {events.length}
                        </div>
                        <div className="text-sm uppercase tracking-widest text-muted-foreground mb-8">
                          Events &amp; Counting
                        </div>
                        <div className="h-px w-24 bg-accent mx-auto mb-8" />
                        <div className="font-serif text-2xl sm:text-3xl text-primary leading-tight mb-4">
                          Arab Capital Markets
                          <br />
                          Events Program
                        </div>
                        <div className="text-base text-muted-foreground">Across the Arab Region</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── 3. Upcoming Events ──────────────────────────────────────────── */}
      {upcomingEvents.length > 0 && (!upcomingSec || upcomingSec.is_visible) && (
        <div id="upcoming-events">
          <EventsShowcase
            events={upcomingEvents}
            mounted={mounted}
            eyebrow={upcomingSec?.eyebrow ?? "Coming soon"}
            title={upcomingSec?.title ?? "Upcoming Events"}
            subtitle={
              upcomingSec?.subtitle ??
              "Future UASA conferences and gatherings. Select one to explore its agenda, speakers, and venue."
            }
            onSelectEvent={(event) => selectEvent(event.id)}
          />
        </div>
      )}

      {/* ── 4. Stats ────────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 md:py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background to-secondary/50">
        <div className="max-w-7xl mx-auto">
          {/* <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              { icon: Calendar, label: "Events",    value: events.length || "—" },
              { icon: Users,    label: "Speakers",  value: speakers.length },
              { icon: Clock,    label: "Sessions",  value: agenda.length },
              { icon: Globe,    label: "Locations", value: locationCount || "20+" },
            ].map((stat, index) => (
              <div
                key={stat.label}
                className={`text-center p-6 rounded-2xl bg-card border border-border shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
                style={{ animationDelay: `${0.6 + index * 0.1}s` }}
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="font-serif text-3xl sm:text-4xl text-primary mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div> */}
        </div>
      </section>

      {/* ── 6. Speakers ─────────────────────────────────────────────────── */}
      {speakers.length > 0 && (!speakersSec || speakersSec.is_visible) && (
        <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-secondary/50 to-background relative">
          <div className="max-w-7xl mx-auto">
            <div
              className={`flex flex-col sm:flex-row sm:items-end justify-between mb-10 sm:mb-12 gap-4 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
              style={{ animationDelay: "0.1s" }}
            >
              <div>
                <div className="inline-block px-3 py-1 rounded-full bg-accent/10 text-accent text-[10px] sm:text-xs uppercase tracking-[0.3em] mb-3 sm:mb-4">
                  {speakersSec?.badge_text ?? ""}
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-primary">
                  {speakersSec?.heading ?? "Distinguished Speakers"}
                </h2>
              </div>
              <Button
                asChild
                variant="outline"
                className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-all w-fit"
              >
                <Link to="/speakers">
                  {speakersSec?.cta_primary_label ?? "View All Speakers"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>

            <div
              className={`relative px-0 md:px-12 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
              style={{ animationDelay: "0.2s" }}
            >
              <Carousel opts={{ align: "start", loop: true }} className="w-full">
                <CarouselContent className="-ml-2 md:-ml-4">
                  {speakers.slice(0, 8).map((s) => (
                    <CarouselItem
                      key={s.id}
                      className="pl-2 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/4"
                    >
                      <Link
                        to="/speakers/$speakerId"
                        params={{ speakerId: s.id.toString() }}
                        className="group block h-full"
                      >
                        <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 p-0 flex flex-col border-border/50 cursor-pointer h-full">
                          <div className="relative aspect-square bg-gradient-to-br from-primary/90 to-primary/60 flex items-center justify-center overflow-hidden flex-shrink-0">
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            {resolveSpeakerImageUrl(s.image_url) ? (
                              <img
                                src={resolveSpeakerImageUrl(s.image_url)!}
                                alt={s.name}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              />
                            ) : (
                              <div className="font-serif text-4xl sm:text-5xl md:text-6xl text-accent/90">
                                {initials(s.name)}
                              </div>
                            )}
                          </div>
                          <div className="p-4 sm:p-5 flex-1 flex flex-col justify-center bg-card">
                            <h3 className="font-serif text-sm sm:text-base text-primary leading-snug mb-2 group-hover:text-accent transition-colors">
                              {s.name}
                            </h3>
                            <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                              {s.role}
                            </p>
                          </div>
                        </Card>
                      </Link>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="hidden md:flex left-0 top-1/2 -translate-y-1/2 h-10 w-10 border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground" />
                <CarouselNext className="hidden md:flex right-0 top-1/2 -translate-y-1/2 h-10 w-10 border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground" />
              </Carousel>
            </div>
          </div>
        </section>
      )}

      {/* ── 7. Sponsors ─────────────────────────────────────────────────── */}
      {sponsors.length > 0 && (
        <section className="py-14 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background to-secondary/30">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div
              className={`text-center mb-10 sm:mb-12 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
              style={{ animationDelay: "0.1s" }}
            >
              {/* <div className="inline-block px-4 py-1.5 rounded-full border border-accent/40 bg-accent/10 text-accent text-[10px] sm:text-xs uppercase tracking-[0.3em] mb-4">
                In Partnership With
              </div> */}
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-primary mb-3">
                Sponsors
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto text-sm sm:text-base">
                Made possible by the support of leading institutions from across the Arab region.
              </p>
            </div>

            {/* Logo grid */}
            <div
              className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-5 mb-8 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
              style={{ animationDelay: "0.2s" }}
            >
              {sponsors.map((s) => (
                <Link
                  key={s.id}
                  to="/sponsors/$sponsorId"
                  params={{ sponsorId: s.id.toString() }}
                  className="group block"
                >
                  <Card className="flex items-center justify-center p-5 sm:p-6 min-h-[100px] sm:min-h-[120px] bg-card border-border/50 hover:border-accent/50 hover:shadow-lg transition-all duration-300 group-hover:-translate-y-0.5">
                    {s.image_url ? (
                      <img
                        src={s.image_url}
                        alt={s.name}
                        className="max-h-14 max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <span className="font-serif text-sm text-primary text-center leading-snug group-hover:text-accent transition-colors">
                        {s.name}
                      </span>
                    )}
                  </Card>
                </Link>
              ))}
            </div>

            {/* View all link */}
            <div className="text-center">
              <Button
                asChild
                variant="outline"
                className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-all"
              >
                <Link to="/sponsors">
                  View All Sponsors <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* ── 8. CTA banner ───────────────────────────────────────────────── */}
      {(!ctaSec || ctaSec.is_visible) && (
        <section className="relative py-20 sm:py-24 md:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/90 to-accent/80" />
          <div className="absolute inset-0 bg-[url('/assets/images/banner1.jpg')] bg-cover bg-center opacity-10" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          <div className="absolute top-0 left-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-primary/30 rounded-full blur-3xl" />

          <div
            className={`relative max-w-4xl mx-auto text-center text-white ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
            style={{ animationDelay: "0.1s" }}
          >
            <div className="inline-block px-4 py-2 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm text-accent text-xs uppercase tracking-[0.3em] mb-6">
              {ctaSec?.badge_text ?? "Join Us"}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight">
              {ctaSec?.heading ?? "Be Part of the Next Event"}
            </h2>
            <p className="text-white/80 mb-8 sm:mb-10 text-base sm:text-lg md:text-xl max-w-2xl mx-auto">
              {ctaSec?.body_text ??
                "Secure your place at the leading gatherings of Arab capital markets leaders, regulators, and innovators."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-10 sm:px-12 h-14 sm:h-16 text-base sm:text-lg shadow-2xl shadow-accent/30 hover:shadow-accent/50 transition-all hover:scale-105"
              >
                {(ctaSec?.cta_primary_href ?? "#upcoming-events").startsWith("/") ? (
                  <Link to={ctaSec?.cta_primary_href ?? "/"}>
                    {ctaSec?.cta_primary_label ?? "Browse Events"}
                  </Link>
                ) : (
                  <a href={ctaSec?.cta_primary_href ?? "#upcoming-events"}>
                    {ctaSec?.cta_primary_label ?? "Browse Events"}
                  </a>
                )}
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:text-white h-14 sm:h-16 px-10 sm:px-12 text-base sm:text-lg transition-all hover:scale-105"
              >
                {(ctaSec?.cta_secondary_href ?? "/contact").startsWith("/") ? (
                  <Link to={ctaSec?.cta_secondary_href ?? "/contact"}>
                    {ctaSec?.cta_secondary_label ?? "Contact Us"}
                  </Link>
                ) : (
                  <a href={ctaSec?.cta_secondary_href ?? "/contact"}>
                    {ctaSec?.cta_secondary_label ?? "Contact Us"}
                  </a>
                )}
              </Button>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
